import type React from 'react';
import type { ElementType } from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

import type { Color } from '#/ui/types';
import type { OverrideProps } from '#/ui/utils/types';
import Box from './box';

export type LinkUnderline = 'none' | 'hover' | 'always';

export interface LinkOwnProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
  /**
   * The color of the d-link.
   * @default 'primary'
   */
  color?: Color | 'inherit';
  /**
   * Controls when the d-link should have an underline.
   * @default 'hover'
   */
  underline?: LinkUnderline;
  /**
   * The URL to d-link to when the d-link is clicked.
   */
  href?: string;
  /**
   * If `true`, the component will be disabled.
   * @default false
   */
  disabled?: boolean;
}

export type LinkTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'a',
> = {
  props: AdditionalProps & LinkOwnProps;
  defaultComponent: RootComponent;
};

export type LinkProps<
  RootComponent extends React.ElementType = LinkTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<LinkTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

function Link<C extends ElementType = 'a'>({
  children,
  component = 'a',
  color = 'primary',
  underline = 'none',
  disabled = false,
  className,
  href,
  ...props
}: LinkProps<C>) {
  return (
    <Box
      component={component}
      className={twMerge(
        'd-link',
        colorMap[color],
        underlineMap[underline],
        disabled && 'opacity-50 pointer-events-none cursor-default',
        className
      )}
      href={href}
      {...props}
    >
      {children}
    </Box>
  );
}

Link.displayName = 'Link';

export default Link;

/*
|------------------
| Internals
|------------------
*/

const colorMap: Record<Required<LinkOwnProps>['color'], string> = {
  primary: 'd-link-primary',
  secondary: 'd-link-secondary',
  accent: 'd-link-accent',
  success: 'd-link-success',
  error: 'd-link-error',
  warning: 'd-link-warning',
  info: 'd-link-info',
  inherit: 'text-inherit hover:opacity-70',
};

const underlineMap: Record<LinkUnderline, string> = {
  none: 'no-underline',
  hover: 'no-underline hover:underline',
  always: 'underline',
};
